import { MarkButton, MarkInput, MarkLabel } from '../../../components/MarkControls';
import React, { useId, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { toast } from 'sonner';
import P from '@hero/ui-kit/typography/P';
import H from '@hero/ui-kit/typography/H';
import Section from '@hero/ui-kit/layout/Section';
import useTheme from '@hero/branding/theme';
import * as styles from './style.module.css';
import useSegmentDraft from '../../../hooks/useSegmentDraft';
import useUnsavedChangesRegistration from '../../../hooks/useUnsavedChangesRegistration';
import useSegmentAttributes from '../../../api/useSegmentAttributes';
import useSegmentPreview from '../../../api/useSegmentPreview';
import useCreateSegment from '../../../api/useCreateSegment';
import useUpdateSegment from '../../../api/useUpdateSegment';
import { segmentKeys } from '../../../api/queryKeys';
import TargetKindSelect from './TargetKindSelect';
import MatchToggle from './MatchToggle';
import SourceSegmentSelect from './SourceSegmentSelect';
import FilterList from './FilterList';
import PreviewPanel from './PreviewPanel';
import UsedByModal from '../UsedByModal';
import { SEGMENT_ERROR_LABELS } from '../../../constants';
import { getSegmentErrorCode } from '../segmentErrors';
import { InertContainer } from '../segmentForm';
import { validateSegmentFilters } from './validation';
import type {
    SegmentFilter,
    SegmentMatch,
    SegmentTargetKind,
    SegmentDetail,
    SegmentPreviewResponse,
    SegmentUsage
} from '../../../types';

type CreateProps = {
    mode: 'create';
    segmentId?: undefined;
    initialValues?: undefined;
    usedBy?: undefined;
};

type EditProps = {
    mode: 'edit';
    segmentId: number;
    initialValues: {
        name: string;
        targetKind: SegmentTargetKind;
        match: SegmentMatch;
        filters: SegmentFilter[];
        sourceSegmentId: number | null;
    };
    usedBy?: SegmentUsage;
};

type Props = CreateProps | EditProps;

type PreviewResult = {
    requestIdentity: string;
    data: SegmentPreviewResponse;
};

const SegmentBuilder: React.FC<Props> = (props) => {
    const { mode, segmentId } = props;
    const { colors, borders } = useTheme();
    const navigate = useNavigate();
    const formId = useId();

    const queryClient = useQueryClient();
    const draft = useSegmentDraft(mode === 'edit' ? props.initialValues : undefined);

    const {
        data: attributesResponse,
        error: attributesError,
        isFetching: attributesFetching
    } = useSegmentAttributes();
    const attributes = attributesResponse?.data?.attributes ?? [];
    const attributesReady = !!attributesResponse;

    const handleRefreshAttributes = () => {
        queryClient.invalidateQueries({ queryKey: segmentKeys.attributes() });
    };

    const previewMutation = useSegmentPreview();
    const createMutation = useCreateSegment();
    const updateMutation = useUpdateSegment();

    const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
    const [submittedPreviewIdentity, setSubmittedPreviewIdentity] = useState<string | null>(null);
    const [showForceConfirm, setShowForceConfirm] = useState(false);
    const [forceUsedBy, setForceUsedBy] = useState<SegmentUsage | null>(null);

    const isSaving = createMutation.isPending || updateMutation.isPending;

    const clearUnsavedChanges = useUnsavedChangesRegistration(
        'segment-builder',
        draft.dirty,
        isSaving
    );

    const filterErrors = useMemo(
        () => (attributesReady ? validateSegmentFilters(draft.filters, attributes) : []),
        [attributes, attributesReady, draft.filters]
    );

    const canSave =
        !!draft.name.trim() &&
        !!draft.targetKind &&
        attributesReady &&
        draft.filters.length > 0 &&
        filterErrors.length === 0 &&
        (mode === 'create' || draft.dirty) &&
        !isSaving;

    const canPreview =
        attributesReady &&
        draft.filters.length > 0 &&
        !!draft.targetKind &&
        filterErrors.length === 0;

    const previewRequest = useMemo(
        () => ({
            target_kind: draft.targetKind as SegmentTargetKind,
            match: draft.match,
            filters: draft.filters,
            source_segment_id: draft.sourceSegmentId
        }),
        [draft.filters, draft.match, draft.sourceSegmentId, draft.targetKind]
    );
    const previewRequestIdentity = JSON.stringify(previewRequest);
    const previewData =
        previewResult?.requestIdentity === previewRequestIdentity ? previewResult.data : null;

    const handlePreview = () => {
        const submittedRequest = previewRequest;
        const requestIdentity = previewRequestIdentity;
        setSubmittedPreviewIdentity(requestIdentity);
        setPreviewResult((current) =>
            current?.requestIdentity === requestIdentity ? null : current
        );
        previewMutation.mutate(submittedRequest, {
            onSuccess: (response) => setPreviewResult({ requestIdentity, data: response.data }),
            onError: () =>
                setPreviewResult((current) =>
                    current?.requestIdentity === requestIdentity ? null : current
                )
        });
    };

    const handleSaveError = async (error: unknown) => {
        const code = getSegmentErrorCode(error);
        if (code === 'invalid_attribute' || code === 'segment_attribute_unavailable') {
            toast.error(SEGMENT_ERROR_LABELS[code] ?? code);
            await queryClient.invalidateQueries({ queryKey: segmentKeys.attributes() });
        }
        if (mode === 'edit' && code === 'segment_in_use') {
            const detailKey = segmentKeys.detail(segmentId!);
            await queryClient.invalidateQueries({ queryKey: detailKey });
            const refreshedDetail =
                queryClient.getQueryData<AxiosResponse<SegmentDetail>>(detailKey)?.data;
            setForceUsedBy(refreshedDetail?.used_by ?? props.usedBy ?? null);
            setShowForceConfirm(true);
        }
    };

    const finishSave = (savedSegmentId: number) => {
        flushSync(() => {
            draft.commit();
            clearUnsavedChanges();
        });
        navigate(`/mark/segments/${savedSegmentId}`);
    };

    const performSave = (force: boolean) => {
        if (mode === 'create') {
            createMutation.mutate(
                {
                    name: draft.name,
                    target_kind: draft.targetKind as SegmentTargetKind,
                    match: draft.match,
                    filters: draft.filters,
                    source_segment_id: draft.sourceSegmentId
                },
                {
                    onSuccess: (response) => {
                        toast.success(
                            `Saved. Matches: ${response.data.match_count.toLocaleString()}.`
                        );
                        finishSave(response.data.segment_id);
                    },
                    onError: handleSaveError
                }
            );
        } else {
            updateMutation.mutate(
                {
                    segment_id: segmentId!,
                    name: draft.name,
                    match: draft.match,
                    filters: draft.filters,
                    source_segment_id: draft.sourceSegmentId ?? undefined,
                    force: force || undefined
                },
                {
                    onSuccess: (response) => {
                        const r = response.data;
                        let msg = `Saved. Matches: ${r.match_count.toLocaleString()}.`;
                        if (r.affected_drips_count || r.affected_blasts_count) {
                            msg += ` Affected drips: ${r.affected_drips_count ?? 0}, blasts: ${r.affected_blasts_count ?? 0}.`;
                        }
                        toast.success(msg);
                        setShowForceConfirm(false);
                        finishSave(segmentId!);
                    },
                    onError: async (error) => {
                        setShowForceConfirm(false);
                        await handleSaveError(error);
                    }
                }
            );
        }
    };

    const handleSave = () => {
        if (mode === 'edit' && props.usedBy) {
            const usedByCount = props.usedBy.drips.length + props.usedBy.blasts.length;
            if (usedByCount > 0) {
                setForceUsedBy(props.usedBy);
                setShowForceConfirm(true);
                return;
            }
        }
        performSave(false);
    };

    const activeMutation = mode === 'create' ? createMutation : updateMutation;
    const errorMessage = activeMutation.error
        ? (getSegmentErrorCode(activeMutation.error) ?? 'Save failed')
        : null;

    const previewErrorCode = previewMutation.error
        ? getSegmentErrorCode(previewMutation.error)
        : null;
    const previewError = previewMutation.error
        ? submittedPreviewIdentity === previewRequestIdentity && previewErrorCode
            ? (SEGMENT_ERROR_LABELS[previewErrorCode] ?? previewErrorCode)
            : submittedPreviewIdentity === previewRequestIdentity
              ? 'Preview failed'
              : null
        : null;
    const previewIsPending =
        previewMutation.isPending && submittedPreviewIdentity === previewRequestIdentity;

    const handleTargetKindChange = (targetKind: SegmentTargetKind | '') => {
        draft.setTargetKind(targetKind);
        draft.setSourceSegmentId(null);
    };

    return (
        <InertContainer disabled={isSaving} aria-busy={isSaving} className={styles.builder}>
            <Section
                border
                noDefaultPadding
                subtheme={['regular', 'background']}
                className={styles.panel}
                styles={borders.variant.secondary}
            >
                <div className={styles.panelHeader}>
                    <div>
                        <H role="h5" styles={{ marginBottom: '0.8rem' }}>
                            Segment details
                        </H>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{ color: colors.neutrals.textBeta }}
                        >
                            Give your segment a name and choose its audience.
                        </P>
                    </div>
                </div>
                <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                        <MarkLabel htmlFor={`${formId}-name`} style={{ margin: 0 }}>
                            Segment name
                        </MarkLabel>
                        <MarkInput
                            id={`${formId}-name`}
                            value={draft.name}
                            onChange={(event) => draft.setName(event.currentTarget.value)}
                            placeholder="e.g. Active members"
                            aria-required
                            style={{ width: '100%' }}
                        />
                    </div>
                    <TargetKindSelect
                        value={draft.targetKind}
                        onChange={handleTargetKindChange}
                        disabled={mode === 'edit'}
                    />
                </div>
                <SourceSegmentSelect
                    value={draft.sourceSegmentId}
                    onChange={draft.setSourceSegmentId}
                    targetKind={draft.targetKind}
                    excludeId={segmentId}
                    lockedSource={mode === 'edit' && props.initialValues.sourceSegmentId !== null}
                />
            </Section>

            <Section
                border
                noDefaultPadding
                subtheme={['regular', 'background']}
                className={styles.panel}
                styles={borders.variant.secondary}
            >
                <div className={styles.panelHeader}>
                    <div>
                        <H role="h5" styles={{ marginBottom: '0.8rem' }}>
                            Audience rules
                        </H>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{ color: colors.neutrals.textBeta }}
                        >
                            Choose how your filters work together, then add your conditions.
                        </P>
                    </div>
                    <MarkButton
                        onClick={handleRefreshAttributes}
                        disabled={attributesFetching}
                        variant="secondary"
                    >
                        {attributesFetching ? 'Loading fields…' : 'Refresh fields'}
                    </MarkButton>
                </div>
                <MatchToggle value={draft.match} onChange={draft.setMatch} />
                {attributesError && (
                    <P
                        role="alert"
                        noDefaultMargin
                        size="small"
                        styles={{ color: colors.status.fail.alpha, marginBottom: '1.2rem' }}
                    >
                        Filter fields could not be loaded. Refresh and try again.
                    </P>
                )}
                <FilterList
                    idPrefix={`${formId}-filter`}
                    filters={draft.keyedFilters}
                    attributes={attributes}
                    onUpdate={draft.updateFilter}
                    onRemove={draft.removeFilter}
                    onAdd={draft.addFilter}
                    disabled={!draft.targetKind || attributes.length === 0}
                    disabledReason={
                        !draft.targetKind
                            ? 'Choose an audience type above to start adding filters.'
                            : attributesFetching
                              ? 'Loading the available filter fields…'
                              : 'Filter fields are unavailable. Refresh fields to try again.'
                    }
                />
                {filterErrors.length > 0 && (
                    <div role="alert" style={{ marginTop: '1.6rem' }}>
                        <P
                            noDefaultMargin
                            strong
                            size="small"
                            styles={{ color: colors.status.fail.alpha }}
                        >
                            These filters need attention before you can save or preview:
                        </P>
                        <ul style={{ margin: '0.8rem 0 0', paddingLeft: '1.8rem' }}>
                            {filterErrors.map((error) => (
                                <li key={`${error.index}-${error.field}`}>
                                    <MarkButton
                                        variant="secondary"
                                        onClick={() =>
                                            document
                                                .getElementById(
                                                    `${formId}-filter-${error.index}-edit`
                                                )
                                                ?.click()
                                        }
                                    >
                                        Filter {error.index + 1}: {error.message}
                                    </MarkButton>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Section>

            <PreviewPanel
                data={previewData}
                isLoading={previewIsPending}
                error={previewError}
                onPreview={handlePreview}
                disabled={!canPreview}
                targetKind={draft.targetKind}
            />

            <footer
                className={styles.footer}
                style={{
                    ...borders.bottom,
                    borderBottom: 0,
                    borderTop: `0.2rem solid ${colors.neutrals.borderBeta}`,
                    backgroundColor: colors.neutrals.background
                }}
            >
                <P noDefaultMargin size="small" styles={{ color: colors.neutrals.textBeta }}>
                    {draft.filters.length
                        ? `${draft.filters.length} ${draft.filters.length === 1 ? 'filter' : 'filters'} · Match ${draft.match === 'ALL' ? 'all' : 'any'}`
                        : 'Add at least one filter to save this segment.'}
                </P>
                <div className={styles.actions}>
                    {draft.dirty && (
                        <MarkButton onClick={draft.reset} variant="secondary" size="regular">
                            Reset changes
                        </MarkButton>
                    )}
                    <MarkButton
                        onClick={handleSave}
                        disabled={!canSave}
                        variant="primary"
                        size="regular"
                    >
                        {isSaving
                            ? 'Saving…'
                            : mode === 'create'
                              ? 'Create segment'
                              : 'Save changes'}
                    </MarkButton>
                </div>
            </footer>
            {errorMessage && (
                <P
                    role="alert"
                    noDefaultMargin
                    size="small"
                    styles={{ color: colors.status.fail.alpha }}
                >
                    {SEGMENT_ERROR_LABELS[errorMessage] ?? errorMessage}
                </P>
            )}
            {showForceConfirm && mode === 'edit' && forceUsedBy && (
                <UsedByModal
                    usedBy={forceUsedBy}
                    isPending={updateMutation.isPending}
                    onConfirm={() => performSave(true)}
                    onCancel={() => setShowForceConfirm(false)}
                    action="save"
                />
            )}
        </InertContainer>
    );
};

export default SegmentBuilder;
